import { ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sidebar } from "@/components/layout/Sidebar";
import { Topbar } from "@/components/layout/Topbar";
import { BottomNav } from "@/components/layout/BottomNav";
import { SubscriptionBanner } from "@/components/SubscriptionBanner";
import { SidebarProvider, useSidebar } from "@/contexts/SidebarContext";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { LoadingLogo } from "@/components/atoms/LoadingLogo";

interface DashboardLayoutProps {
  children: ReactNode;
}

function DashboardLayoutContent({ children }: DashboardLayoutProps) {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#000000]">
        <LoadingLogo size="lg" variant="square" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#000000] text-white font-sans">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-[#19F58C] focus:text-black focus:rounded-lg focus:outline-none"
      >
        Ir para o conteúdo
      </a>
      <Sidebar />

      {/* INNFLOW DS v3.0 Ambient Glow — Liquid Glass */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% -20%, rgba(143,0,255,0.06), transparent 50%), radial-gradient(ellipse at 50% -20%, rgba(25,245,140,0.08), transparent 50%), radial-gradient(ellipse at 100% 100%, rgba(0,102,255,0.05), transparent 50%)",
        }}
        aria-hidden="true"
      />

      {/* Content area: margin-left 64px desktop; padding for topbar + bottom nav */}
      <div
        className={cn(
          "flex-1 flex flex-col transition-all duration-300",
          "lg:ml-16",
        )}
      >
        <Topbar />
        <SubscriptionBanner />

        <main
          id="main"
          className="flex-1 overflow-y-auto pb-14 lg:pb-0"
          tabIndex={-1}
        >
          <div className="container-desktop px-4 lg:px-8 py-6 lg:py-8">
            {children}
          </div>
        </main>
      </div>

      <BottomNav />
    </div>
  );
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  return (
    <SidebarProvider>
      <DashboardLayoutContent>{children}</DashboardLayoutContent>
    </SidebarProvider>
  );
}
